import React from 'react';

export interface ErrorProps extends Error {
	status?: number;
}

export interface SettingsTabProps {
	onSave: () => void;
}

export interface ConfigDataResponse extends Response {
	data?: ConfigData;
	message?: string;
}

export interface BuyPlan extends Response {
	data?: {
		needRedirect?: boolean;
		url: string;
	};
	message?: string;
}

export interface PlanData extends Response {
	data?: PlanInformation;
	message?: string;
}

export interface CancelSubscription extends Response {
	data?: string;
	message?: string;
}

export interface PlanInformation {
	plans: PlanInfo[];
	activePlan: ActivePlan;
}

export interface ActivePlan {
	id: number;
	is_free: boolean;
	expired_at: number | null;
}

export interface PlanInfo {
	id: number;
	name: string;
	price: number;
	description: string;
}

export enum DisplayAt {
	ALL = 'ALL',
	HOME = 'HOME',
	SPECIFIC = 'SPECIFIC',
	CATEGORY = 'CATEGORY',
}

export type SpecificPages = {
	id?: number;
	url: string;
};

export type Category = {
	id?: number;
	name: string;
	externalId: number;
	categoryUrl: string;
};

export enum TimerPosition {
	TOP = 'TOP',
	BOTTOM = 'BOTTOM',
}

export enum CountdownPosition {
	RIGHT_OF_TITLE = 'RIGHT_OF_TITLE',
	LEFT_OF_TITLE = 'LEFT_OF_TITLE',
	ABOVE_TITLE = 'ABOVE_TITLE',
	BELOW_TITLE = 'BELOW_TITLE',
}

export enum AnimationEnum {
	FLIP = 'FLIP',
	INSTANT = 'INSTANT',
	SWIPE = 'SWIPE',
	SLIDE_OUT = 'SLIDE_OUT',
	FADE_IN_OUT = 'FADE_IN_OUT',
	SCROLL = 'SCROLL',
}

export type BackgroundUploadRes = {
	background: {
		background_image: string | null;
		original_image_name: string | null;
	};
};

export type AppearanceConfig = {
	text_alignment: TextAlignment;
	title_font_style: string;
	subtitle_font_style: string;
	button_text_color: string;
	button_bg_color: string;
	font: string;
	background_image: string | null;
	original_image_name: string | null;
	background_color: string;
	timer_window_color: string;
	timer_window_border_width: number;
	timer_window_border_color: string;
	timer_content_spacing_top: number;
	timer_content_spacing_right: number;
	timer_content_spacing_bottom: number;
	timer_content_spacing_left: number;
	countdown_color: string;
	time_units_color: string;
	custom_css: string;
};

export interface ConfigData {
	enable_timer: boolean;
	name: string;
	priority: number;
	display_at: DisplayAt;
	countdown_mode: string;
	from_date?: string; // Date in ISO format
	to_date?: string; // Date in ISO format
	time_frame_type: string;
	time_frame_value?: number;
	interactivity: string;
	url?: string;
	title: string;
	subtitle: string;
	show_days: boolean;
	days_label: string;
	show_hours: boolean;
	hours_label: string;
	show_minutes: boolean;
	minutes_label: string;
	show_seconds: boolean;
	seconds_label: string;
	button_text: string;
	font: string;
	background_color: string;
	timer_window_color: string;
	timer_window_border_width: number;
	timer_window_border_color: string;
	timer_content_spacing_top: number;
	timer_content_spacing_right: number;
	timer_content_spacing_bottom: number;
	timer_content_spacing_left: number;
	countdown_color: string;
	time_units_color: string;
	title_font_style: string;
	subtitle_font_style: string;
	button_bg_color: string;
	button_text_color: string;
	specificPages: SpecificPages[];
	categories: Category[];
	background_image: string | null;
	original_image_name: string | null;
	timer_position: TimerPosition;
	countdown_position: CountdownPosition;
	animation: AnimationEnum;
	text_alignment: TextAlignment;
	timer_mode: TimerMode;
	message_text: string;
	custom_css: string;
	template_id: string | null;
	mobile_use_desktop_settings: boolean;
	mobile_appearance: AppearanceConfig | null;
}

export enum Fields {
	TITLE = 'title',
	SUBTITLE = 'subtitle',
}

export type HandleFieldChangeType = <T extends keyof ConfigData>(field: T, value: ConfigData[T]) => void;

export type CategoryItem = {
	id: number;
	name: string;
	categoryUrl: string;
	children: CategoryItem[];
};

export enum TextAlignment {
	LEFT = 'LEFT',
	CENTER = 'CENTER',
	RIGHT = 'RIGHT',
}

export enum TimerMode {
	HIDE = 'HIDE',
	RESTART = 'RESTART',
	MESSAGE = 'MESSAGE',
}

export type AlignmentIconProps = React.JSX.IntrinsicAttributes &
	React.SVGProps<SVGSVGElement> & {
		active?: boolean;
	};

export enum Route {
	EDIT = 'EDIT',
	CREATE = 'CREATE',
}

export enum ContentLength {
	TITLE = 170,
	SUB_TITLE = 190,
	BUTTON_TEXT = 64,
	LABELS_TIMING = 24,
}

export enum StartToFinishDateRangeSaveIssue {
	MISSING = 'missing',
	UNPARSABLE = 'unparsable',
	ERROR = 'error',
}

export enum StartToFinishDateRangeErrorMessage {
	REQUIRED = 'This is a required field.',
	MISSING_RANGE = 'Both dates are required and must be valid.',
	UNPARSABLE = 'Please fill out this field.',
	ERROR = 'Date and time must be in the future.',
}
